"use client";

// @storybook-internal wiring chassis: binds the shared machine to Next
// router + tRPC. Its visual surfaces (transcript, composer, chips, widgets)
// are catalogued through their own deterministic stories.

import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";

import type {
  T_ChatV8RunMode,
  T_ChatV8RunModeOption,
} from "@lattice/core-lib/types/chat-v8";
import { E_UsageFeature } from "@lattice/core-lib/types/usage";
import { isChatV8ThreadRoutePending } from "@lattice/shared-frontend/chat-v8";
import type { T_ShellChatSurface } from "@lattice/shared-frontend/shell";
import {
  getUsageFeatureProjection,
  getUsageWarningPercent,
} from "@lattice/shared-frontend/usage";
import { E_Page, routes } from "@lattice/frontend/lib/routes/routes";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import {
  ChatComposer,
  type T_ChatComposerAttachments,
} from "./components/ChatComposer";
import { useChatV8Attachments } from "./use-chat-v8-attachments";
import {
  ConversationTranscript,
  type T_ConversationTranscriptProps,
} from "./components/ConversationTranscript";
import { PromptChipRow } from "./components/PromptChipRow";
import {
  persistChatV8RunMode,
  readStoredChatV8RunMode,
} from "./run-mode-preference";
import { useMobileKeyboardViewport } from "./mobile-keyboard-viewport";
import { useChatV8, type T_UseChatV8Result } from "./use-chat-v8";
import "./chat-v8.css";

const STARTER_PROMPTS = [
  "Summarize today’s market",
  "Review my portfolio risk",
  "What should I watch this week?",
];

export type T_SendPrompt = (prompt: string) => Promise<void>;

export type T_ChatV8ClientProps = {
  autoSubmit?: boolean;
  /**
   * Home/empty-state page content. Receives the canonical send handler so
   * page-level affordances (e.g. the home suggestion pills, which are page
   * content below holdings — not dock chrome) start threads through the same
   * path as the composer.
   */
  emptyContent?: ReactNode | ((sendPrompt: T_SendPrompt) => ReactNode);
  composerSuggestionSurface?: T_ShellChatSurface;
  onMessageStarted?: () => void;
  onThreadListChanged?: () => void;
  prefilledMessage?: string;
  renderStarterPrompts?: (
    sendPrompt: (prompt: string) => Promise<void>,
  ) => ReactNode;
  threadCuid: string | null;
};

export type T_ChatV8SurfaceProps = {
  attachments?: T_ChatComposerAttachments;
  availableRunModes?: readonly T_ChatV8RunModeOption[];
  chat: T_ConversationTranscriptProps["chat"] &
    Pick<T_UseChatV8Result, "canUseComposer" | "handleCancelRun">;
  emptyContent?: ReactNode | ((sendPrompt: T_SendPrompt) => ReactNode);
  composerSuggestionSurface?: T_ShellChatSurface;
  expectedThreadCuid: string | null;
  onRunModeChange?: (mode: T_ChatV8RunMode) => void;
  onSend: (message: string) => Promise<void>;
  prefilledMessage?: string;
  renderStarterPrompts?: (
    sendPrompt: (prompt: string) => Promise<void>,
  ) => ReactNode;
  runMode?: T_ChatV8RunMode;
  usageWarningPercent?: number;
};

/** Production visual composition of transcript, full-width scroll owner, and
 * docked composer. Network and route wiring stay in ChatV8Client. */
export function ChatV8Surface({
  attachments,
  availableRunModes = [],
  chat,
  composerSuggestionSurface,
  emptyContent,
  expectedThreadCuid,
  onRunModeChange,
  onSend,
  prefilledMessage,
  renderStarterPrompts,
  runMode,
  usageWarningPercent,
}: T_ChatV8SurfaceProps) {
  const routeIsPending = isChatV8ThreadRoutePending({
    expectedThreadCuid,
    state: chat.state,
  });
  const clientRef = useRef<HTMLElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const [composerFocused, setComposerFocused] = useState(false);
  const mobileFocusActive =
    composerFocused && composerSuggestionSurface !== undefined;
  const { ensureBaseline, prepareBaseline } = useMobileKeyboardViewport({
    active: mobileFocusActive,
    anchorRef: clientRef,
  });
  // The jump-to-bottom control sits above the dock, whose height varies with
  // starter prompts, staged attachments, and composer growth. Publish the
  // measured height so CSS can offset without hardcoding a guess.
  // eslint-disable-next-line no-restricted-syntax -- ResizeObserver is the DOM measurement boundary for dock-relative layout.
  useEffect(() => {
    const dock = dockRef.current;
    const client = clientRef.current;
    if (!dock || !client) return;

    const publishDockHeight = () => {
      client.style.setProperty(
        "--chat-v8-dock-height",
        `${Math.round(dock.getBoundingClientRect().height)}px`,
      );
    };

    publishDockHeight();
    const observer = new ResizeObserver(publishDockHeight);
    observer.observe(dock);
    return () => observer.disconnect();
  }, []);

  const handleComposerFocusChange = useCallback(
    (focused: boolean) => {
      if (focused && composerSuggestionSurface !== undefined) ensureBaseline();
      setComposerFocused(focused);
    },
    [composerSuggestionSurface, ensureBaseline],
  );
  const starterPromptsEligible =
    !routeIsPending &&
    expectedThreadCuid === null &&
    chat.renderableItems.length === 0 &&
    !mobileFocusActive;
  // Resolve the actual node once: a caller that supplies renderStarterPrompts
  // owns the decision to show them, so returning undefined must hide the row
  // rather than fall back to the generic prompts (RAI-1089 — hiding the home
  // chips on holdings expansion previously revealed STARTER_PROMPTS instead).
  const starterPrompts = starterPromptsEligible ? (
    renderStarterPrompts !== undefined ? (
      (renderStarterPrompts(onSend) ?? null)
    ) : (
      <PromptChipRow onPress={onSend} prompts={STARTER_PROMPTS} />
    )
  ) : null;
  return (
    <main
      className={[
        "chat-v8-client",
        starterPrompts !== null ? "chat-v8-client--with-starter-prompts" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      ref={clientRef}
    >
      <ConversationTranscript
        chat={chat}
        emptyContent={
          typeof emptyContent === "function"
            ? emptyContent(onSend)
            : emptyContent
        }
        expectedThreadCuid={expectedThreadCuid}
        onSendFollowUp={
          // Suggested follow-ups send into the CURRENT thread (unlike home
          // chips, which start a new one). Withheld while a run is active so
          // a tap cannot race an in-flight response.
          chat.canUseComposer &&
          chat.state.status !== "sending" &&
          chat.state.status !== "streaming"
            ? (prompt) => void onSend(prompt)
            : undefined
        }
      />
      {mobileFocusActive ? (
        <button
          aria-label="Dismiss message composer"
          className="chat-v8-focus-scrim"
          onClick={() => composerInputRef.current?.blur()}
          type="button"
        />
      ) : null}
      <div className="chat-v8-dock" ref={dockRef}>
        <div className="chat-v8-dock__content">
          {starterPrompts !== null ? (
            <div className="chat-v8-starter-prompts">{starterPrompts}</div>
          ) : null}
          <ChatComposer
            attachments={attachments}
            availableRunModes={availableRunModes}
            disabled={routeIsPending || !chat.canUseComposer}
            focused={composerFocused}
            onFocusChange={handleComposerFocusChange}
            onFocusPrepare={
              composerSuggestionSurface === undefined
                ? undefined
                : prepareBaseline
            }
            onInputElementChange={(element) => {
              composerInputRef.current = element;
            }}
            onCancelRun={
              chat.state.activeRun !== null &&
              (chat.state.status === "sending" ||
                chat.state.status === "streaming")
                ? () => void chat.handleCancelRun()
                : undefined
            }
            onRunModeChange={onRunModeChange}
            onSend={onSend}
            prefilledMessage={prefilledMessage}
            runMode={runMode}
            suggestionSurface={composerSuggestionSurface}
            usageWarningPercent={usageWarningPercent}
          />
          <p className="chat-v8-disclaimer">
            Prism can make mistakes. Verify important information.
          </p>
        </div>
      </div>
    </main>
  );
}

export function ChatV8Client({
  autoSubmit = false,
  composerSuggestionSurface,
  emptyContent,
  onMessageStarted,
  onThreadListChanged,
  prefilledMessage,
  renderStarterPrompts,
  threadCuid,
}: T_ChatV8ClientProps) {
  const router = useRouter();
  const usageQuery = trpcApi.usage.get_usage_stats.useQuery(undefined, {
    staleTime: 30_000,
  });
  const prismUsage = getUsageFeatureProjection(
    usageQuery.data?.features ?? [],
    E_UsageFeature.PRISM_QUERIES,
  );
  const usageWarningPercent = getUsageWarningPercent(prismUsage);
  const [runMode, setRunMode] = useState(readStoredChatV8RunMode);
  const handleRunModeChange = useCallback((mode: T_ChatV8RunMode) => {
    setRunMode(mode);
    persistChatV8RunMode(mode);
  }, []);
  const chat = useChatV8({
    mode: runMode,
    onThreadListChanged,
    routeThreadCuid: threadCuid,
    updateRouteThreadCuid: (next) =>
      router.replace(next ? routes.shellThread(next) : `${E_Page.Shell}/new`),
  });
  const attachments = useChatV8Attachments({
    capabilities: chat.state.capabilities,
    transport: chat.transport,
  });
  const sendMessage = useCallback(
    async (message: string) => {
      onMessageStarted?.();
      await chat.handleSendMessage(message, {
        attachments: attachments.takeSendAttachments(),
      });
    },
    [attachments, chat, onMessageStarted],
  );

  // eslint-disable-next-line no-restricted-syntax -- URL prefill is an external route boundary.
  useEffect(() => {
    if (autoSubmit && prefilledMessage?.trim())
      void sendMessage(prefilledMessage);
  }, [autoSubmit, prefilledMessage, sendMessage]);

  return (
    <ChatV8Surface
      attachments={
        attachments.enabled
          ? {
              accept: attachments.accept,
              canSend: attachments.canSend,
              onRemove: attachments.remove,
              onRetry: attachments.retry,
              onStageFiles: attachments.stageFiles,
              staged: attachments.staged,
            }
          : undefined
      }
      availableRunModes={chat.state.capabilities?.availableRunModes}
      chat={chat}
      composerSuggestionSurface={composerSuggestionSurface}
      emptyContent={emptyContent}
      expectedThreadCuid={threadCuid}
      onRunModeChange={handleRunModeChange}
      onSend={sendMessage}
      prefilledMessage={autoSubmit ? "" : prefilledMessage}
      renderStarterPrompts={renderStarterPrompts}
      runMode={runMode}
      usageWarningPercent={usageWarningPercent ?? undefined}
    />
  );
}
